/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';
import {
  motion, AnimatePresence, Reorder, useDragControls,
} from 'framer-motion';
import { Dialog, Menu, MenuItem } from '@mui/material';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import TvRoundedIcon from '@mui/icons-material/TvRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { AppContext } from 'Context';
import { dataFetcher, endpoints } from 'Api';
import { Trash2, ArrowDownUp } from 'lucide-react';
import { AnimatedFilePenLine } from 'Components/reactbits/AnimatedIcons';
import { setCondense } from 'Utils/scrollCondense';
import { markSectionExplored, EXPLORED_SECTION } from 'Utils/onboardingChecklist';
import CategoryEditModal from './CategoryEditModal';
import { loadRowContent, contentReorderType } from './contentLoaders';
import { trackSave } from './saveBus';
import SaveIndicator from './SaveIndicator';
import LivePreviewPop from './LivePreviewPop';
import confirmStyles from '../../videos/components/AlbumPhotosView.module.css';
import styles from './ProjectorPreview.module.css';

// Delete endpoint + payload per content type — same contract the old
// /content-layout/:type detail page used for its Delete action.
const DELETE_CONFIG = {
  category: { endpoint: endpoints.deleteMyCategory, payload: (id) => ({ catId: id }) },
  playlist: { endpoint: endpoints.deleteMyPlayList, payload: (id) => ({ id }) },
  album: { endpoint: endpoints.deleteMyAlbum, payload: (id) => ({ album_id: id }) },
};

// Flat horizontal strip of 100x66 thumbnails (replaces the WebGL circular-gallery:
// bend/no-wobble/exact-size/snug requirements describe a flat strip, and a DOM strip
// hits exact pixels + won't hijack the page). Vertical wheel scrolls it horizontally.
// One draggable content tile (whole tile is the drag handle).
const FlatTile = ({ item, onSettle, onStart }) => {
  const controls = useDragControls();
  return (
    <Reorder.Item
      as="div"
      value={item}
      dragListener={false}
      dragControls={controls}
      onDragStart={() => onStart && onStart()}
      onDragEnd={() => onSettle(item.id)}
      onPointerDown={(e) => controls.start(e)}
      className={styles.flatTile}
      whileDrag={{ scale: 1.06, zIndex: 5 }}
      transition={{ type: 'spring', stiffness: 500, damping: 40 }}
      style={{ touchAction: 'none', cursor: 'grab' }}
    >
      <span className={styles.flatThumb} style={{ backgroundImage: `url(${item.image})` }} />
      {item.text ? <span className={styles.flatCaption} title={item.text}>{item.text}</span> : null}
    </Reorder.Item>
  );
};

// Flat horizontal strip of thumbnails. Read-only by default; pass onReorder +
// onSettle to make the tiles drag-to-rearrange (auto-saved).
const FlatCarousel = ({ items, onReorder, onSettle, onStart }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        el.scrollLeft += e.deltaY;
        if (e.cancelable) e.preventDefault();
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);
  if (onReorder) {
    return (
      <Reorder.Group ref={ref} as="div" axis="x" values={items} onReorder={onReorder} className={styles.flatCarousel}>
        {items.map((c) => (<FlatTile key={c.id} item={c} onSettle={onSettle} onStart={onStart} />))}
      </Reorder.Group>
    );
  }
  return (
    <div ref={ref} className={styles.flatCarousel}>
      {items.map((c, i) => (
        <div key={`${c.image}-${i}`} className={styles.flatTile}>
          <span className={styles.flatThumb} style={{ backgroundImage: `url(${c.image})` }} />
          {c.text ? <span className={styles.flatCaption} title={c.text}>{c.text}</span> : null}
        </div>
      ))}
    </div>
  );
};

// A single skeleton row shown while the page's content is still loading.
const ShimmerRow = () => (
  <div className={styles.shRow}>
    <span className={styles.shThumb} />
    <span className={styles.shLines}>
      <span className={styles.shLineA} />
      <span className={styles.shLineB} />
    </span>
    <span className={styles.shChip} />
  </div>
);

// One draggable category tile in the horizontal projector-style nav. Mirrors
// the /profile category tiles (darkened cover image + label), whole-tile drag.
const CategoryTile = ({ cat, onDragSettle, onDragStart }) => {
  const controls = useDragControls();
  const bg = cat.imagekit_icon || cat.icon || null;
  return (
    <Reorder.Item
      as="div"
      value={cat}
      dragListener={false}
      dragControls={controls}
      onDragStart={() => onDragStart && onDragStart()}
      onDragEnd={() => onDragSettle(cat.id)}
      onPointerDown={(e) => controls.start(e)}
      className={styles.catTile}
      whileDrag={{ scale: 1.05, zIndex: 6 }}
      transition={{ type: 'spring', stiffness: 500, damping: 40 }}
      style={{
        touchAction: 'none',
        ...(bg
          ? { backgroundImage: `linear-gradient(rgba(0,0,0,0.32), rgba(0,0,0,0.62)), url(${bg})` }
          : {}),
      }}
      title={cat.title || 'Untitled'}
    >
      <span className={styles.catTileGrip}><DragIndicatorRoundedIcon fontSize="inherit" /></span>
      <span className={styles.catTileName}>{cat.title || 'Untitled'}</span>
    </Reorder.Item>
  );
};

// Horizontal, drag-to-rearrange category nav under the projector name. A plain
// mouse wheel only reports vertical delta (unlike a trackpad's two-finger swipe),
// so translate that into horizontal scroll here. Arrow keys scroll it too — the
// container focuses itself on hover (focus-follows-mouse) so a plain hover is
// enough, no click/drag needed first.
const CategoryNav = ({
  items, onReorder, onDragSettle, onDragStart,
}) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        el.scrollLeft += e.deltaY;
        if (e.cancelable) e.preventDefault();
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);
  const onKeyDown = (e) => {
    if (e.key === 'ArrowRight') {
      ref.current?.scrollBy({ left: 188, behavior: 'smooth' });
    } else if (e.key === 'ArrowLeft') {
      ref.current?.scrollBy({ left: -188, behavior: 'smooth' });
    } else {
      return;
    }
    e.preventDefault();
  };
  // preventScroll: focus() normally scrolls the page to bring the target into
  // view — the strip is already visible on hover, so that jump is unwanted.
  const onMouseEnter = () => { ref.current?.focus({ preventScroll: true }); };
  const onMouseLeave = () => { if (document.activeElement === ref.current) ref.current.blur(); };
  return (
    <Reorder.Group
      ref={ref}
      as="div"
      axis="x"
      values={items}
      onReorder={onReorder}
      className={styles.catNav}
      tabIndex={0}
      onKeyDown={onKeyDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {items.map((cat) => (
        <CategoryTile key={cat.id} cat={cat} onDragSettle={onDragSettle} onDragStart={onDragStart} />
      ))}
    </Reorder.Group>
  );
};

// One album card in the horizontal albums row (preview mode). Draggable via its
// grip; clicking the card expands its photos below the whole row.
const AlbumCard = ({
  album, active, onOpen, onDragSettle, onDragStart,
}) => {
  const controls = useDragControls();
  const bg = album.imagekit_icon || album.icon || null;
  const count = album.photo_count ?? album.photos_count ?? album.count;
  return (
    <Reorder.Item
      as="div"
      value={album}
      dragListener={false}
      dragControls={controls}
      onDragStart={() => onDragStart && onDragStart()}
      onDragEnd={() => onDragSettle(album.id)}
      className={cn(styles.albumCard, { [styles.albumCardActive]: active })}
      whileDrag={{ scale: 1.05, zIndex: 6 }}
      transition={{ type: 'spring', stiffness: 500, damping: 40 }}
      style={{ touchAction: 'none' }}
    >
      <span className={styles.albumGrip} onPointerDown={(e) => controls.start(e)}>
        <DragIndicatorRoundedIcon fontSize="inherit" />
      </span>
      <button type="button" className={styles.albumOpenBtn} onClick={onOpen}>
        <span className={styles.albumThumb} style={bg ? { backgroundImage: `url(${bg})` } : undefined} />
        <span className={styles.albumName} title={album.title || 'Untitled'}>{album.title || 'Untitled'}</span>
        {count != null && <span className={styles.albumCount}>{`${count} ${count === 1 ? 'photo' : 'photos'}`}</span>}
      </button>
    </Reorder.Item>
  );
};

// Photo Albums in preview: all albums in ONE horizontal row; the selected album
// expands below the row to show its photos.
const AlbumsStrip = ({
  items, userId, onReorder, onRowSettle, onDragStart,
}) => {
  const [openId, setOpenId] = useState(null);
  const [photos, setPhotos] = useState({});
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!openId || photos[openId]) return undefined;
    let alive = true;
    setLoading(true);
    loadRowContent({ reorderType: 'album', itemId: openId, userId })
      .then((list) => { if (alive) setPhotos((p) => ({ ...p, [openId]: list || [] })); })
      .catch(() => { if (alive) setPhotos((p) => ({ ...p, [openId]: [] })); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [openId]); // eslint-disable-line react-hooks/exhaustive-deps
  const openAlbum = items.find((a) => a.id === openId);
  const openPhotos = photos[openId] || [];
  return (
    <div className={styles.albumsWrap}>
      <Reorder.Group as="div" axis="x" values={items} onReorder={onReorder} className={styles.albumsRow}>
        {items.map((a) => (
          <AlbumCard
            key={a.id}
            album={a}
            active={openId === a.id}
            onOpen={() => setOpenId((cur) => (cur === a.id ? null : a.id))}
            onDragStart={onDragStart}
            onDragSettle={() => onRowSettle(a)}
          />
        ))}
      </Reorder.Group>
      <AnimatePresence initial={false}>
        {openAlbum && (
          <motion.div
            key={openId}
            layout
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.26, ease: [0.22, 0.61, 0.36, 1] }}
            style={{ overflow: 'hidden' }}
          >
            <div className={styles.albumPhotos}>
              {loading && openPhotos.length === 0 ? (
                <div className={styles.empty}>Loading photos…</div>
              ) : openPhotos.length === 0 ? (
                <div className={styles.empty}>No photos in this album yet</div>
              ) : (
                <FlatCarousel items={openPhotos} />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const SORT_OPTIONS = [
  { k: 'az', label: 'Name · A → Z' },
  { k: 'za', label: 'Name · Z → A' },
  { k: 'dateAsc', label: 'Date published · Oldest first' },
  { k: 'dateDesc', label: 'Date published · Newest first' },
  { k: 'reverse', label: 'Reverse current order' },
];

// Second child — a reorderable row; expands to its content carousel.
const PreviewRow = ({
  item, section, expanded, onToggle, onDragSettle, onDragStart, onOpenSettings, onEdit, onDelete, canDelete, userId,
  onMoveFirst, onMoveUp, onMoveDown, onMoveLast, canUp, canDown, onContentReorder,
}) => {
  const controls = useDragControls();
  const bg = item.imagekit_icon || item.icon || null;
  const rowCount = section.key === 'albums'
    ? (item.photo_count ?? item.photos_count ?? null)
    : (item.video_count ?? item.videos_count ?? item.count ?? null);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const contentRef = useRef(content);
  contentRef.current = content;
  // Snapshot of the order when a drag begins, so the live preview can animate
  // the moved item from its old to new position.
  const preDragRef = useRef(null);

  // Hand the content reorder up to the page, which persists it AND assembles the
  // full projector-home payload for the cinematic preview pop.
  const persistContent = (movedId) => {
    onContentReorder({
      section,
      rowItem: item,
      movedId,
      order: contentRef.current || [],
      prevOrder: preDragRef.current,
    });
  };

  useEffect(() => {
    if (!expanded || content !== null || loading) return undefined;
    let alive = true;
    setLoading(true);
    loadRowContent({ reorderType: section.reorderType, itemId: item.id, userId })
      .then((list) => { if (alive) setContent(list); })
      .catch(() => { if (alive) setContent([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [expanded]);

  return (
    <Reorder.Item
      as="div"
      value={item}
      dragListener={false}
      dragControls={controls}
      onDragStart={() => onDragStart && onDragStart()}
      onDragEnd={onDragSettle}
      className={`${styles.row} ${expanded ? styles.rowActive : ''}`}
      whileDrag={{ scale: 1.006, zIndex: 5 }}
      transition={{ type: 'spring', stiffness: 500, damping: 40 }}
    >
      <div className={styles.rowHead}>
        <button
          type="button"
          className={styles.rowDrag}
          aria-label="Drag to reorder"
          onPointerDown={(e) => controls.start(e)}
          style={{ touchAction: 'none' }}
        >
          <DragIndicatorRoundedIcon fontSize="small" />
        </button>

        <span
          className={styles.rowThumb}
          style={bg ? { backgroundImage: `url(${bg})` } : { backgroundColor: `${section.accent}22` }}
        />

        <button type="button" className={styles.rowTitleBtn} onClick={onToggle}>
          <span className={styles.rowTitle}>{item.title || 'Untitled'}</span>
          {rowCount != null && (
            <span className={styles.rowSub}>
              {`${rowCount} ${section.key === 'albums' ? 'photos' : 'videos'}`}
            </span>
          )}
        </button>

        {/* Hidden for now — drag handle covers reordering; may bring these back later.
        <span className={styles.rowMove}>
          <button
            type="button"
            className={styles.moveBtn}
            disabled={!canUp}
            onClick={onMoveFirst}
            aria-label="Move to top"
            title="Move to top"
          >
            <ChevronsUp size={15} strokeWidth={2.4} />
          </button>
          <button
            type="button"
            className={styles.moveBtn}
            disabled={!canUp}
            onClick={onMoveUp}
            aria-label="Move up"
            title="Move up"
          >
            <ArrowUp size={15} strokeWidth={2.4} />
          </button>
          <button
            type="button"
            className={styles.moveBtn}
            disabled={!canDown}
            onClick={onMoveDown}
            aria-label="Move down"
            title="Move down"
          >
            <ArrowDown size={15} strokeWidth={2.4} />
          </button>
          <button
            type="button"
            className={styles.moveBtn}
            disabled={!canDown}
            onClick={onMoveLast}
            aria-label="Move to bottom"
            title="Move to bottom"
          >
            <ChevronsDown size={15} strokeWidth={2.4} />
          </button>
        </span>
        */}

        <button
          type="button"
          className={styles.rowEdit}
          aria-label={`Edit ${section.singular}`}
          title={`Edit ${section.singular}`}
          onClick={() => onEdit(section, item)}
        >
          <AnimatedFilePenLine size={16} />
        </button>

        {canDelete && (
          <button
            type="button"
            className={styles.rowDelete}
            aria-label={`Delete ${section.singular}`}
            title={`Delete ${section.singular}`}
            onClick={() => onDelete(item)}
          >
            <Trash2 size={16} strokeWidth={2.2} />
          </button>
        )}

        {section.key === 'albums' && (
          <button
            type="button"
            className={styles.rowSettings}
            aria-label="Album slideshow settings"
            title="Album slideshow settings"
            onClick={() => onOpenSettings(item)}
          >
            <SettingsRoundedIcon fontSize="small" />
          </button>
        )}

        <button
          type="button"
          className={`${styles.rowExpand} ${expanded ? styles.rowExpandOpen : ''}`}
          onClick={onToggle}
          aria-label={expanded ? 'Collapse' : 'Expand'}
          title={expanded ? 'Collapse' : 'Expand'}
        >
          <ChevronRightRoundedIcon fontSize="small" />
        </button>
      </div>

      {expanded && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={styles.carouselWrap}>
          {loading && (
            <div className={styles.carousel}>
              {Array.from({ length: 6 }, (_, i) => (<span key={i} className={styles.tile} />))}
            </div>
          )}
          {!loading && content && content.length > 0 && (
            <FlatCarousel
              items={content}
              onReorder={setContent}
              onSettle={persistContent}
              onStart={() => { preDragRef.current = [...(contentRef.current || [])]; }}
            />
          )}
          {!loading && content && content.length === 0 && (
            <div className={styles.emptyRow}>
              {`No ${section.singular === 'album' ? 'photos' : 'videos'} in this ${section.singular} yet`}
            </div>
          )}
        </motion.div>
      )}
    </Reorder.Item>
  );
};

// One content-type accordion (Categories / Playlists / Photo Albums). The header
// toggles it; the body holds the reorderable PreviewRows for that type.
const SectionAccordion = ({
  section, open, onToggle, expandedId, setExpandedId, onReorderItems, onRowSettle,
  onOpenSettings, onEdit, onDelete, onMove, onSort, defaultCategoryName, userId, loading,
  onMoveTypeUp, onMoveTypeDown, canTypeUp, canTypeDown, onEditType, onContentReorder,
  albumsRow, typeDragControls,
}) => {
  const [sortAnchor, setSortAnchor] = useState(null);
  const [headHover, setHeadHover] = useState(false);
  const items = section.items || [];
  // Snapshot of the row order when a drag begins, so the preview pop can animate
  // the moved row from its old position to the new one.
  const preDragRows = useRef(null);
  return (
    <div className={cn(styles.acc, { [styles.accOpen]: open })}>
      <div className={styles.accHead}>
        <button
          type="button"
          className={styles.accDrag}
          aria-label={`Drag to reorder ${section.title}`}
          onPointerDown={(e) => typeDragControls?.start(e)}
          style={{ touchAction: 'none' }}
        >
          <DragIndicatorRoundedIcon fontSize="small" />
        </button>
        <button
          type="button"
          className={styles.accHeadMain}
          onClick={onToggle}
          onMouseEnter={() => setHeadHover(true)}
          onMouseLeave={() => setHeadHover(false)}
          aria-expanded={open}
        >
          <span className={styles.accIcon} style={{ color: section.accent }}>
            <section.Icon size={18} strokeWidth={2.2} animate={headHover || open} />
          </span>
          <span className={styles.accTitle}>{section.title}</span>
          <span className={styles.accCount}>{section.count ?? items.length}</span>
        </button>
        {items.length > 1 && (
          <>
            <button
              type="button"
              className={styles.orderBtn}
              onClick={(e) => setSortAnchor(e.currentTarget)}
            >
              <ArrowDownUp size={15} strokeWidth={2.2} />
              Quick order by
            </button>
            <Menu
              anchorEl={sortAnchor}
              open={Boolean(sortAnchor)}
              onClose={() => setSortAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ sx: { borderRadius: 2, mt: 0.5, minWidth: 232 } }}
            >
              {SORT_OPTIONS.map((o) => (
                <MenuItem
                  key={o.k}
                  sx={{ fontWeight: 600, fontSize: 13 }}
                  onClick={() => { setSortAnchor(null); onSort(o.k); }}
                >
                  {o.label}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
        <button
          type="button"
          className={styles.accEditText}
          onClick={onEditType}
          aria-label={`Edit ${section.title} (open detail view)`}
          title={`Edit ${section.title}`}
        >
          <AnimatedFilePenLine size={15} />
          Edit
        </button>
        {/* Hidden for now — drag handle covers reordering; may bring these back later.
        <span className={styles.accTypeMove}>
          <button
            type="button"
            className={styles.moveBtn}
            disabled={!canTypeUp}
            onClick={onMoveTypeUp}
            aria-label={`Move ${section.title} up`}
            title="Move up"
          >
            <ArrowUp size={15} strokeWidth={2.4} />
          </button>
          <button
            type="button"
            className={styles.moveBtn}
            disabled={!canTypeDown}
            onClick={onMoveTypeDown}
            aria-label={`Move ${section.title} down`}
            title="Move down"
          >
            <ArrowDown size={15} strokeWidth={2.4} />
          </button>
        </span>
        */}
        <button
          type="button"
          className={cn(styles.accChevron, { [styles.accChevronOpen]: open })}
          onClick={onToggle}
          aria-label={open ? 'Collapse' : 'Expand'}
        >
          <ChevronRightRoundedIcon fontSize="small" />
        </button>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="accBody"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.26, ease: [0.22, 0.61, 0.36, 1] }}
            style={{ overflow: 'hidden' }}
          >
            <div className={styles.accBody}>
              {loading && items.length === 0 ? (
                Array.from({ length: 3 }, (_, i) => <ShimmerRow key={i} />)
              ) : items.length === 0 ? (
                <div className={styles.empty}>{`No ${section.title.toLowerCase()} yet`}</div>
              ) : albumsRow ? (
                <AlbumsStrip
                  items={items}
                  userId={userId}
                  onReorder={onReorderItems}
                  onDragStart={() => { preDragRows.current = [...items]; }}
                  onRowSettle={(album) => onRowSettle(album, preDragRows.current)}
                />
              ) : (
                <Reorder.Group
                  as="div"
                  axis="y"
                  values={items}
                  onReorder={onReorderItems}
                  className={styles.rows}
                >
                  {items.map((item, idx) => (
                    <PreviewRow
                      key={item.id}
                      item={item}
                      section={section}
                      expanded={expandedId === item.id}
                      onToggle={() => setExpandedId((cur) => (cur === item.id ? null : item.id))}
                      onDragStart={() => { preDragRows.current = [...items]; }}
                      onDragSettle={() => onRowSettle(item, preDragRows.current)}
                      onOpenSettings={onOpenSettings}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      canDelete={!(section.reorderType === 'category' && item.title === defaultCategoryName)}
                      userId={userId}
                      onMoveFirst={() => onMove(item, 'first')}
                      onMoveUp={() => onMove(item, 'up')}
                      onMoveDown={() => onMove(item, 'down')}
                      onMoveLast={() => onMove(item, 'last')}
                      canUp={idx > 0}
                      canDown={idx < items.length - 1}
                      onContentReorder={onContentReorder}
                    />
                  ))}
                </Reorder.Group>
              )}
              {section.hasMore && <ShimmerRow key="loadMore" />}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Drag-to-reorder wrapper for a whole content-type accordion (Categories /
// Playlists / Photo Albums among each other). dragListener is disabled so a
// drag only starts from the handle button inside SectionAccordion's header.
const SortableSectionAccordion = ({ section, onTypeDragStart, onTypeDragSettle, ...rest }) => {
  const typeDragControls = useDragControls();
  return (
    <Reorder.Item
      as="div"
      value={section}
      dragListener={false}
      dragControls={typeDragControls}
      onDragStart={onTypeDragStart}
      onDragEnd={() => onTypeDragSettle(section.key)}
      whileDrag={{ scale: 1.008, zIndex: 5, boxShadow: '0 14px 34px rgba(16,24,40,0.14)' }}
      transition={{ type: 'spring', stiffness: 500, damping: 40 }}
    >
      <SectionAccordion section={section} typeDragControls={typeDragControls} {...rest} />
    </Reorder.Item>
  );
};

// Best-effort published-date accessor across the various field names the API uses.
const getItemDate = (it) => {
  const d = it.published_at || it.created_at || it.createdAt || it.created || it.date;
  const t = d ? new Date(d).getTime() : 0;
  return Number.isNaN(t) ? 0 : t;
};

const ProjectorPreview = ({
  sections: initialSections, onOpenAlbumSettings, loading, onRefresh, projectorName, previewBg,
}) => {
  const { setAppSnackbar, userDetails, defaultCategoryName } = useContext(AppContext);
  const navigate = useNavigate();
  const [sections, setSections] = useState(initialSections);
  // Which type accordions are open (all collapsed by default).
  const [openSet, setOpenSet] = useState(() => new Set());
  const [expandedId, setExpandedId] = useState(null);
  const [showProjector, setShowProjector] = useState(false);
  // Row delete — { section, item } while the confirm dialog is open.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Row edit — { section, item } while the edit modal is open.
  const [editTarget, setEditTarget] = useState(null);
  const projectorLabel = projectorName || 'Your Projector';

  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;

  // The condensed ("preview") chrome is now toggled explicitly via the Preview
  // button in the header — no longer driven by scroll (that fought the layout
  // reflow and felt glitchy). Just reset the bus when the tab unmounts.
  const bodyRef = useRef(null);
  // Pre-drag snapshot of the category-nav order, for the preview pop's old→new
  // movement.
  const preDragCatRef = useRef(null);
  // Pre-drag snapshot of the type (accordion) order, for persisting the drag settle.
  const preDragSectionsRef = useRef(null);
  useEffect(() => () => setCondense(false), []);

  // Re-sync only when the underlying data actually changes (initialSections is a new
  // array every parent render, so depending on it directly would thrash + clobber
  // local reorders). A stable signature of keys + item ids drives the sync instead.
  const dataKey = initialSections
    .map((s) => `${s.key}:${s.items.length}:${s.items.map((i) => i.id).join(',')}`)
    .join('|');
  useEffect(() => { setSections(initialSections); }, [dataKey]);

  // Infinite scroll: only `.body` scrolls (the accordions grow to their natural
  // height inside it), so watch it for "near bottom" and page in the next batch
  // for whichever open section still has more rows on the server.
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return undefined;
    const onScroll = () => {
      if (el.scrollHeight - el.scrollTop - el.clientHeight > 200) return;
      sections.forEach((s) => {
        if (openSet.has(s.key) && s.hasMore && s.onLoadMore) s.onLoadMore();
      });
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [sections, openSet]);

  const toggleSection = (key) => setOpenSet((prev) => {
    const n = new Set(prev);
    if (n.has(key)) n.delete(key); else n.add(key);
    return n;
  });

  const setSectionItems = (key, items) => setSections((prev) => prev.map((s) => (s.key === key ? { ...s, items } : s)));

  // Map a list of rows/sections to preview tiles for the live pop.
  const toPreviewItems = (arr) => (arr || []).map((it) => ({
    id: it.id, image: it.imagekit_icon || it.icon, text: it.title,
  }));
  const rowPreview = (section, movedId, orderList, prevList) => ({
    kind: section.reorderType, title: section.title, items: toPreviewItems(orderList), prevItems: prevList ? toPreviewItems(prevList) : undefined, movedId,
  });
  const typePreview = (list, movedKey, prevList) => ({
    kind: 'type',
    title: 'Projector sections',
    items: list.map((s) => ({ id: s.key, image: s.items?.[0]?.imagekit_icon || s.items?.[0]?.icon, text: s.title })),
    prevItems: prevList ? prevList.map((s) => ({ id: s.key, image: s.items?.[0]?.imagekit_icon || s.items?.[0]?.icon, text: s.title })) : undefined,
    movedId: movedKey,
  });

  // Cinematic content-reorder payload: the projector home (name + category nav +
  // a few rows to load) plus the affected row's old/new order for the zoom+swap.
  const buildContentPreview = (section, rowItem, movedId, order, prevOrder) => {
    const affectedRowKey = `${section.key}:${rowItem.id}`;
    const homeRows = sections
      .flatMap((s) => (s.items || []).map((it) => ({
        id: `${s.key}:${it.id}`, itemId: it.id, reorderType: s.reorderType, title: it.title,
      })))
      .slice(0, 5);
    if (!homeRows.some((r) => r.id === affectedRowKey)) {
      homeRows.unshift({
        id: affectedRowKey, itemId: rowItem.id, reorderType: section.reorderType, title: rowItem.title,
      });
    }
    const cats = sections.find((s) => s.key === 'categories')?.items || [];
    return {
      kind: 'content',
      projectorName: projectorLabel,
      userId: userDetails?.myId,
      nav: toPreviewItems(cats),
      rows: homeRows,
      affected: {
        rowId: affectedRowKey,
        itemId: rowItem.id,
        title: rowItem.title,
        items: toPreviewItems(order),
        prevItems: prevOrder ? toPreviewItems(prevOrder) : undefined,
        movedId,
      },
    };
  };

  const handleContentReorder = ({ section, rowItem, movedId, order, prevOrder }) => {
    // Ticks "Rearrange" off the dashboard getting-started checklist.
    markSectionExplored(EXPLORED_SECTION.rearrange);
    const pos = order.findIndex((c) => c.id === movedId);
    const afterId = pos > 0 ? order[pos - 1]?.id : null;
    trackSave(dataFetcher(endpoints.reorderItem, {
      type: contentReorderType(section.reorderType),
      item_id: movedId,
      parent_id: rowItem.id,
      ...(afterId ? { after_id: afterId } : {}),
    }).catch(() => {}), buildContentPreview(section, rowItem, movedId, order, prevOrder));
  };

  // Persist a single row's new position within its type (after_id = row now above it).
  const persistRow = (section, movedId, afterId, preview) => {
    markSectionExplored(EXPLORED_SECTION.rearrange);
    const p = dataFetcher(endpoints.reorderItem, {
      type: section.reorderType,
      item_id: movedId,
      ...(afterId ? { after_id: afterId } : {}),
    });
    trackSave(p, preview);
    p.then((r) => { if (!r?.success) setAppSnackbar({ isVisible: true, type: 'error', message: 'Failed to save order' }); })
      .catch(() => setAppSnackbar({ isVisible: true, type: 'error', message: 'Failed to save order' }));
  };

  // Persist a full re-order (used by "Quick order by") by chaining after_id down the list.
  const persistFullOrder = (section, items) => {
    markSectionExplored(EXPLORED_SECTION.rearrange);
    const chain = items.reduce(
      (acc, it, idx) => acc.then(() => dataFetcher(endpoints.reorderItem, {
        type: section.reorderType,
        item_id: it.id,
        ...(idx ? { after_id: items[idx - 1].id } : {}),
      })),
      Promise.resolve(),
    );
    trackSave(chain);
    return chain.catch(() => setAppSnackbar({ isVisible: true, type: 'error', message: 'Failed to save order' }));
  };

  // Persist the content-type (accordion) order.
  const persistTypeOrderFrom = (list, movedKey, prevList) => {
    const map = {};
    list.forEach((s, idx) => { if (s.contentTypeId) map[s.contentTypeId] = idx + 1; });
    if (Object.keys(map).length) {
      markSectionExplored(EXPLORED_SECTION.rearrange);
      trackSave(dataFetcher(endpoints.reOrderMyContentType, { items: map }).catch(() => {}), movedKey ? typePreview(list, movedKey, prevList) : undefined);
    }
  };

  // Move a whole content-type accordion up/down among the three.
  const moveType = (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= sections.length) return;
    const prev = sections;
    const next = [...sections];
    [next[index], next[target]] = [next[target], next[index]];
    setSections(next);
    persistTypeOrderFrom(next, prev[index].key, prev);
  };

  // Drag-to-reorder settle for a whole content-type accordion: framer's
  // onReorder already live-updated `sections`, so persist the freshest order.
  const settleTypeDrag = (movedKey) => {
    persistTypeOrderFrom(sectionsRef.current, movedKey, preDragSectionsRef.current);
  };

  // Auto-save a drag/settle using the freshest order in the ref.
  const persistRowSettle = (section, movedId, prevList) => {
    const cur = sectionsRef.current.find((s) => s.key === section.key)?.items || [];
    const pos = cur.findIndex((it) => it.id === movedId);
    persistRow(section, movedId, pos > 0 ? cur[pos - 1]?.id : null, rowPreview(section, movedId, cur, prevList));
  };

  // Quick reorder of a row within its section: 'first' | 'up' | 'down' | 'last'.
  const moveRow = (section, item, action) => {
    const items = section.items || [];
    const from = items.findIndex((it) => it.id === item.id);
    if (from < 0) return;
    let to = from;
    if (action === 'first') to = 0;
    else if (action === 'last') to = items.length - 1;
    else if (action === 'up') to = from - 1;
    else if (action === 'down') to = from + 1;
    if (to < 0 || to >= items.length || to === from) return;
    const next = [...items];
    next.splice(to, 0, next.splice(from, 1)[0]);
    setSectionItems(section.key, next);
    persistRow(section, item.id, to > 0 ? next[to - 1].id : null, rowPreview(section, item.id, next, items));
  };

  const applySort = (section, kind) => {
    const items = [...(section.items || [])];
    if (kind === 'az') items.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    else if (kind === 'za') items.sort((a, b) => (b.title || '').localeCompare(a.title || ''));
    else if (kind === 'dateAsc') items.sort((a, b) => getItemDate(a) - getItemDate(b));
    else if (kind === 'dateDesc') items.sort((a, b) => getItemDate(b) - getItemDate(a));
    else if (kind === 'reverse') items.reverse();
    setSectionItems(section.key, items);
    persistFullOrder(section, items);
  };

  // Delete a category / playlist / album — same endpoints + payloads the old
  // detail-page Delete action used. The row is dropped locally on success and
  // the parent refetches the list so counts stay honest.
  const closeDeleteDialog = () => { if (!deleting) setDeleteTarget(null); };

  const confirmDelete = async () => {
    const { section, item } = deleteTarget || {};
    const config = DELETE_CONFIG[section?.reorderType];
    if (!config || !item?.id) { setDeleteTarget(null); return; }
    setDeleting(true);
    try {
      const res = await dataFetcher(config.endpoint, config.payload(item.id));
      if (res?.success) {
        setSections((prev) => prev.map((s) => (s.key === section.key
          ? { ...s, items: (s.items || []).filter((it) => it.id !== item.id) }
          : s)));
        if (expandedId === item.id) setExpandedId(null);
        setAppSnackbar({ isVisible: true, message: res?.message || 'Deleted successfully' });
        setDeleteTarget(null);
        if (onRefresh) onRefresh(section);
      } else {
        setAppSnackbar({ isVisible: true, type: 'error', message: res?.message || 'Failed to delete' });
      }
    } catch (e) {
      setAppSnackbar({ isVisible: true, type: 'error', message: 'Failed to delete' });
    } finally {
      setDeleting(false);
    }
  };

  // Mock "view on projector" content: real names, shimmer thumbnails.
  const catSection = sections.find((s) => s.key === 'categories') || sections[0];
  const navBoxes = (catSection?.items || []).slice(0, 8);
  const mockRows = sections
    .flatMap((s) => (s.items || []).map((it) => ({
      id: `${s.key}-${it.id}`,
      title: it.title || 'Untitled',
      img: it.imagekit_icon || it.icon || null,
      reorderType: s.reorderType,
      itemId: it.id,
    })))
    .slice(0, 5);

  // Real thumbnails for the "View on projector" preview — loaded once the modal
  // opens so it shows actual content instead of permanent shimmer tiles.
  const [previewContent, setPreviewContent] = useState({});
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewRowsKey = mockRows.map((r) => r.id).join('|');
  useEffect(() => {
    if (!showProjector || !mockRows.length) return undefined;
    let alive = true;
    setPreviewLoading(true);
    Promise.all(mockRows.map((r) => loadRowContent({ reorderType: r.reorderType, itemId: r.itemId, userId: userDetails?.myId })
      .then((items) => ({ id: r.id, items: items || [] }))
      .catch(() => ({ id: r.id, items: [] }))))
      .then((results) => {
        if (!alive) return;
        setPreviewContent((prev) => {
          const next = { ...prev };
          results.forEach((x) => { next[x.id] = x.items; });
          return next;
        });
      })
      .finally(() => { if (alive) setPreviewLoading(false); });
    return () => { alive = false; };
  }, [showProjector, previewRowsKey, userDetails?.myId]);

  // In preview mode a projector background colour is applied to the surface so
  // the user sees their actual projector backdrop live. Flip the category
  // heading to light text when that backdrop is dark so it stays readable.
  const previewDark = (() => {
    if (!previewBg) return false;
    const hex = String(previewBg).replace('#', '');
    if (hex.length < 6) return true;
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) < 150;
  })();

  // Adaptive palette for the transparent-card preview surface: light ink on a
  // dark projector backdrop, dark ink on a light one.
  const pvVars = previewBg ? (previewDark ? {
    '--catTitleColor': '#f5f7fb',
    '--catHintColor': 'rgba(245,247,251,0.6)',
    '--pvText': '#f5f7fb',
    '--pvSub': 'rgba(245,247,251,0.72)',
    '--pvBorder': 'rgba(255,255,255,0.32)',
    '--pvBorderStrong': 'rgba(255,255,255,0.55)',
    '--pvHover': 'rgba(255,255,255,0.08)',
    '--pvActive': 'rgba(255,255,255,0.14)',
    '--pvChip': 'rgba(255,255,255,0.16)',
    '--pvStroke': 'rgba(255,255,255,0.75)',
  } : {
    '--catTitleColor': '#15171c',
    '--catHintColor': '#667085',
    '--pvText': '#15171c',
    '--pvSub': '#4a5160',
    '--pvBorder': 'rgba(21,23,28,0.16)',
    '--pvBorderStrong': 'rgba(21,23,28,0.28)',
    '--pvHover': 'rgba(21,23,28,0.05)',
    '--pvActive': 'rgba(21,23,28,0.09)',
    '--pvChip': 'rgba(21,23,28,0.06)',
    '--pvStroke': 'rgba(21,23,28,0.55)',
  }) : undefined;

  return (
    <div
      className={previewBg ? `${styles.preview} ${styles.previewSurface}` : styles.preview}
      style={pvVars}
    >
      {/* Fixed top region — the projector-style horizontal category nav (drag to
          rearrange). Sits OUTSIDE the scroll container so it never scrolls and
          the accordions can never slide behind it. */}
      {catSection && catSection.items.length > 0 && (
        <div className={styles.catNavWrap}>
          <div className={styles.catNavTop}>
            <span className={styles.catNavTitle}>Categories</span>
            <span className={styles.catNavHint}>Drag to rearrange</span>
          </div>
          <CategoryNav
            items={catSection.items}
            onReorder={(next) => setSectionItems('categories', next)}
            onDragStart={() => { preDragCatRef.current = [...catSection.items]; }}
            onDragSettle={(movedId) => persistRowSettle(catSection, movedId, preDragCatRef.current)}
          />
        </div>
      )}

      {/* The scroll region — only the accordion list scrolls. */}
      <div className={styles.body} ref={bodyRef}>
        {/* Categories / Playlists / Photo Albums accordions with their rows.
            Drag the handle to reorder the types; arrows remain as a fallback. */}
        <Reorder.Group as="div" axis="y" values={sections} onReorder={setSections} className={styles.accList}>
          {sections.map((section, idx) => (
            <SortableSectionAccordion
              key={section.key}
              section={section}
              onTypeDragStart={() => { preDragSectionsRef.current = [...sections]; }}
              onTypeDragSettle={settleTypeDrag}
              open={openSet.has(section.key)}
              onToggle={() => toggleSection(section.key)}
              expandedId={expandedId}
              setExpandedId={setExpandedId}
              onReorderItems={(items) => setSectionItems(section.key, items)}
              onRowSettle={(item, prevList) => persistRowSettle(section, item.id, prevList)}
              onOpenSettings={onOpenAlbumSettings}
              onEdit={(sec, it) => setEditTarget({ section: sec, item: it })}
              onDelete={(row) => setDeleteTarget({ section, item: row })}
              onMove={(item, action) => moveRow(section, item, action)}
              onSort={(kind) => applySort(section, kind)}
              onMoveTypeUp={() => moveType(idx, -1)}
              onMoveTypeDown={() => moveType(idx, 1)}
              canTypeUp={idx > 0}
              canTypeDown={idx < sections.length - 1}
              onEditType={() => { if (section.layoutType) navigate(`/content-layout/${section.layoutType}`); }}
              onContentReorder={handleContentReorder}
              albumsRow={Boolean(previewBg) && section.key === 'albums'}
              defaultCategoryName={defaultCategoryName}
              userId={userDetails?.myId}
              loading={loading}
            />
          ))}
        </Reorder.Group>
        <SaveIndicator />
        <LivePreviewPop />
      </div>

      {/* "View on projector" preview — mock TV home with real names + shimmer tiles */}
      <Dialog
        open={showProjector}
        onClose={() => setShowProjector(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#0e0f13', borderRadius: 1.5, backgroundImage: 'none', overflow: 'hidden', maxHeight: '92vh',
          },
        }}
      >
        <div className={styles.tvScreen}>
          <button
            type="button"
            className={styles.tvClose}
            onClick={() => setShowProjector(false)}
            aria-label="Close preview"
          >
            <CloseRoundedIcon fontSize="small" />
          </button>

          <div className={styles.tvTopline}>
            <span className={styles.tvBadge}>
              <TvRoundedIcon fontSize="small" />
              Projector preview
            </span>
            <span className={styles.tvHint}>Live layout · sample content</span>
          </div>

          <h2 className={styles.tvBrand}>{projectorLabel}</h2>

          {navBoxes.length > 0 && (
            <div className={styles.tvNav}>
              {navBoxes.map((b) => (
                <span
                  key={b.id}
                  className={styles.tvNavBox}
                  style={(b.imagekit_icon || b.icon) ? { backgroundImage: `url(${b.imagekit_icon || b.icon})` } : undefined}
                >
                  <span className={styles.tvNavLabel}>{b.title || 'Untitled'}</span>
                </span>
              ))}
            </div>
          )}

          {/* Progressive loader below the nav while the preview pulls content. */}
          {previewLoading && (
            <div className={styles.tvLoadingBar}>
              <span className={styles.tvLoadingDot} />
              Loading your content…
            </div>
          )}

          <div className={styles.tvRows}>
            {mockRows.length === 0 ? (
              <div className={styles.tvEmpty}>Add content to see it on your projector</div>
            ) : (
              mockRows.map((row) => {
                const tiles = previewContent[row.id] || [];
                return (
                  <div key={row.id} className={styles.tvRow}>
                    <span className={styles.tvRowTitle}>{row.title}</span>
                    <div className={styles.tvStrip}>
                      {tiles.length > 0
                        ? tiles.slice(0, 7).map((t, i) => (
                          <span
                            key={i}
                            className={styles.tvTile}
                            style={{
                              animation: 'none',
                              backgroundImage: t.image ? `url(${t.image})` : undefined,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                            }}
                          />
                        ))
                        : Array.from({ length: 7 }, (_, i) => (
                          <span key={i} className={styles.tvTile} style={{ animationDelay: `${i * 0.12}s` }} />
                        ))}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </Dialog>

      {/* In-place edit — name (+ cover image for categories) and a concise
          drag-to-reorder grid of the group's contents. */}
      <CategoryEditModal
        open={Boolean(editTarget)}
        item={editTarget?.item}
        section={editTarget?.section}
        userId={userDetails?.myId}
        onClose={() => setEditTarget(null)}
        onSaved={({ id, title, icon }) => {
          const key = editTarget?.section?.key;
          setSections((prev) => prev.map((s) => (s.key === key
            ? {
              ...s,
              items: (s.items || []).map((it) => (it.id === id
                ? { ...it, title, icon: icon || it.icon, imagekit_icon: icon || it.imagekit_icon }
                : it)),
            }
            : s)));
        }}
      />

      {/* Row delete confirmation — same look as the album/photo bulk-delete dialog */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={closeDeleteDialog}
        maxWidth="xs"
        fullWidth
        aria-labelledby="delete-group-dialog-title"
        PaperProps={{ className: confirmStyles.dialogPaper }}
      >
        <div className={confirmStyles.dialogContainer}>
          <div className={confirmStyles.iconWrap}>
            <Trash2 strokeWidth={2} />
          </div>
          <h3 id="delete-group-dialog-title" className={confirmStyles.dialogTitle}>
            {`Delete ${deleteTarget?.section?.singular || 'item'}?`}
          </h3>
          <p className={confirmStyles.dialogMessage}>
            {deleteTarget?.item?.title
              ? `Are you sure you want to delete "${deleteTarget.item.title}"?`
              : `Are you sure you want to delete this ${deleteTarget?.section?.singular || 'item'}?`}
            <br />
            This action cannot be undone.
          </p>
          <div className={confirmStyles.actionContainer}>
            <button
              type="button"
              className={confirmStyles.cancelBtn}
              onClick={closeDeleteDialog}
              disabled={deleting}
            >
              Go back
            </button>
            <button
              type="button"
              className={confirmStyles.deleteBtn}
              onClick={confirmDelete}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ProjectorPreview;
